// @givmo/design-system — UploadDropzone (SaaS layer image upload control).
//
// One consistent ~104px upload control for the super-admin charity/ETF/celebrity logos + posters
// (previously three divergent hand-rolled buttons: a 56px circle, a 96px rounded-rect, a 120px
// rounded-rect). A labelled, `rounded-xl` dashed dropzone with a placeholder icon + hint, or the
// current image filling the frame. Wraps the hidden `<input type=file>` + the trigger button + the
// image/placeholder so callers pass only an `onFile(file)` handler and the upload mutation stays in
// the feature. Multipart upload behaviour is unchanged — the caller still owns the mutation.
//
// a11y: the trigger is a real `<button>` with an `aria-label`; the file input is visually hidden but
// reachable programmatically. testIds forward to the button / input / image so existing e2e ids hold.
import { forwardRef, useRef } from 'react';
import type { ReactNode } from 'react';
import { ImagePlus } from 'lucide-react';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';
import { Icon } from './icon';

export interface UploadDropzoneProps extends TestIdProps {
  /** Label above the control (s13 / w500 / text-secondary, matching Field). */
  label?: ReactNode;
  /** Accessible label for the trigger button. */
  ariaLabel: string;
  /** Current image src (cache-busted by the caller), or null to show the placeholder. */
  src?: string | null;
  /** Alt text for the image. */
  imageAlt?: string;
  /** Placeholder hint under the icon (e.g. "Add logo"). */
  hint?: string;
  /** `accept` attr for the file input (defaults to images). */
  accept?: string;
  disabled?: boolean;
  /** Called with the picked File; the caller owns the (multipart) upload. */
  onFile: (file: File) => void;
  /** Square edge length in px (defaults to 104 — the unified size). */
  size?: number;
  className?: string;
  /** testId for the hidden file input. */
  inputTestId?: string;
  /** testId for the rendered image. */
  imageTestId?: string;
}

export const UploadDropzone = forwardRef<HTMLButtonElement, UploadDropzoneProps>(
  function UploadDropzone(
    {
      label,
      ariaLabel,
      src,
      imageAlt = '',
      hint = 'Upload image',
      accept = 'image/*',
      disabled = false,
      onFile,
      size = 104,
      className,
      testId,
      inputTestId,
      imageTestId,
    },
    ref,
  ) {
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const onPick = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = ''; // allow re-picking the same file
      if (file) onFile(file);
    };

    return (
      <div className={cn('flex flex-col items-start gap-6', className)}>
        {label != null && (
          <span className="font-sans text-s13 font-w500 tracking-givmo text-text-secondary">
            {label}
          </span>
        )}
        <button
          ref={ref}
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          style={{ width: size, height: size }}
          className={cn(
            'flex shrink-0 flex-col items-center justify-center gap-4 overflow-hidden rounded-xl',
            'border border-dashed border-border-strong bg-surface-sunken text-icon-muted',
            'outline-none transition-colors',
            'hover:border-brand-400 hover:bg-surface-hover hover:text-brand-600',
            'focus-visible:border-brand-500 focus-visible:shadow-ring',
            'disabled:cursor-not-allowed disabled:opacity-60',
            // When an image is present, drop the dashed border + tint so it reads as a framed thumbnail.
            src != null && 'border-solid border-border-subtle bg-surface',
          )}
          aria-label={ariaLabel}
          {...testIdProp(testId)}
        >
          {src != null ? (
            <img
              src={src}
              alt={imageAlt}
              className="size-full object-cover"
              {...testIdProp(imageTestId)}
            />
          ) : (
            <>
              <Icon icon={ImagePlus} size={22} aria-hidden />
              <span className="px-8 text-center font-sans text-s11 tracking-normal">{hint}</span>
            </>
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={onPick}
          {...testIdProp(inputTestId)}
        />
      </div>
    );
  },
);
